import { useRouteLocale } from "@/hooks/useRouteLocale";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/hooks/useAuth";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { USER_ROUTES, ADMIN_ROUTES } from "@/routes";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getDeletedAccounts,
  getDeletedEvents,
  restoreAccount,
  restoreEvent,
  getFeatureFlags,
  updateFeatureFlag,
  getGlobalSettings,
  getManagementUsers,
  getRevenue,
  type SoftDeletedAccount,
  type SoftDeletedEvent,
  type ManagementUser,
  type RevenueResponse,
} from "@/services/api/management";
import { toast } from "sonner";
import { RotateCcw, Loader2, Trash2, User, Calendar, AlertTriangle, Flag, Settings, Users, Link2, ArrowUpDown, ArrowUp, ArrowDown, Search, X, Wallet, Info } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format, parseISO } from "date-fns";
import { ro } from "date-fns/locale";

type ManagementTabValue = "feature-flags" | "settings" | "users" | "revenue" | "accounts" | "events";

const MANAGEMENT_TABS: ManagementTabValue[] = ["users", "revenue", "feature-flags", "settings", "accounts", "events"];
const USERS_SEARCH_DEBOUNCE_MS = 300;

function isManagementTab(value: string | null): value is ManagementTabValue {
  return value !== null && (MANAGEMENT_TABS as string[]).includes(value);
}

export default function Management() {
  const { t } = useTranslation();
  const _locale = useRouteLocale();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const currentLocale = _locale || "ro";
  // date-fns formats in English unless told otherwise (JUB-204)
  const dateLocale = currentLocale === "ro" ? ro : undefined;
  usePageMeta({
    title: t("management.metaTitle"),
    description: t("management.description"),
    locale: currentLocale,
    path: ADMIN_ROUTES.management(currentLocale),
    noindex: true,
  });
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Redirect if not administrator
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    // Check permissions once auth is loaded
    if (!user) {
      // Not logged in - redirect to management login
      navigate(`/${currentLocale}/management/login`, { replace: true });
    } else if (!user.user_admin) {
      // Logged in but not admin - redirect to events
      navigate(USER_ROUTES.events(currentLocale), { replace: true });
    } else {
      // User is admin - allow access
      setCheckingPermissions(false);
    }
  }, [user, authLoading, navigate, currentLocale]);

  // Tab and users search live in the URL (?tab=users&search=<email>) so the
  // admin events list can deep-link to a user by their email (JUB-92).
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: ManagementTabValue = isManagementTab(tabParam) ? tabParam : "users";
  const setActiveTab = (tab: ManagementTabValue) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "users") next.delete("tab");
        else next.set("tab", tab);
        return next;
      },
      { replace: true }
    );
  };
  const urlSearch = searchParams.get("search") ?? "";
  // usersSearchInput = what the admin is typing; usersSearch = debounced value sent to the API.
  const [usersSearchInput, setUsersSearchInput] = useState(urlSearch);
  const [usersSearch, setUsersSearch] = useState(urlSearch);
  const usersSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [accounts, setAccounts] = useState<SoftDeletedAccount[]>([]);
  const [events, setEvents] = useState<SoftDeletedEvent[]>([]);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean> | null>(null);
  const [togglingFlags, setTogglingFlags] = useState<Set<string>>(new Set());
  const [globalSettings, setGlobalSettings] = useState<Record<string, unknown> | null>(null);
  const [usersList, setUsersList] = useState<ManagementUser[]>([]);
  const [revenue, setRevenue] = useState<RevenueResponse | null>(null);
  const [usersCursor, setUsersCursor] = useState<string | null>(null);
  const [usersHasMore, setUsersHasMore] = useState(false);
  const [usersSortField, setUsersSortField] = useState<"user_name" | "user_email" | "events_count" | "created_at">("created_at");
  const [usersSortDirection, setUsersSortDirection] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [cursor, setCursor] = useState<{ accounts: string | null; events: string | null }>({
    accounts: null,
    events: null,
  });
  const [hasMore, setHasMore] = useState<{ accounts: boolean; events: boolean }>({
    accounts: false,
    events: false,
  });
  const [restoreDialog, setRestoreDialog] = useState<{
    open: boolean;
    type: "account" | "event" | null;
    id: string | number | null;
    name: string | null;
  }>({
    open: false,
    type: null,
    id: null,
    name: null,
  });

  useEffect(() => {
    loadData();
  }, [activeTab, usersSortField, usersSortDirection, usersSearch]);

  // Debounce typing -> API search term, and mirror the applied term into the URL.
  useEffect(() => {
    if (usersSearchDebounce.current) clearTimeout(usersSearchDebounce.current);
    usersSearchDebounce.current = setTimeout(() => {
      const term = usersSearchInput.trim();
      setUsersSearch(term);
      setUsersCursor(null);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (term) next.set("search", term);
          else next.delete("search");
          return next;
        },
        { replace: true }
      );
    }, USERS_SEARCH_DEBOUNCE_MS);
    return () => {
      if (usersSearchDebounce.current) clearTimeout(usersSearchDebounce.current);
    };
  }, [usersSearchInput, setSearchParams]);

  // Deep link changed under us (e.g. owner link clicked while already on this page).
  useEffect(() => {
    setUsersSearchInput((current) => (current.trim() === urlSearch ? current : urlSearch));
  }, [urlSearch]);

  const loadData = async (loadMore: boolean = false) => {
    try {
      setLoading(true);
      if (activeTab === "feature-flags") {
        const res = await getFeatureFlags();
        setFeatureFlags(res.flags);
      } else if (activeTab === "settings") {
        const res = await getGlobalSettings();
        setGlobalSettings(res.settings as Record<string, unknown>);
      } else if (activeTab === "users") {
        const response = await getManagementUsers({
          cursor: loadMore ? usersCursor || undefined : undefined,
          sort: usersSortField,
          direction: usersSortDirection,
          search: usersSearch || undefined,
        });
        if (loadMore) {
          setUsersList((prev) => [...prev, ...response.items]);
        } else {
          setUsersList(response.items);
        }
        setUsersCursor(response.cursor || null);
        setUsersHasMore(response.hasMore);
      } else if (activeTab === "revenue") {
        const response = await getRevenue();
        setRevenue(response);
      } else if (activeTab === "accounts") {
        const response = await getDeletedAccounts(
          loadMore ? cursor.accounts || undefined : undefined
        );
        if (loadMore) {
          setAccounts((prev) => [...prev, ...response.items]);
        } else {
          setAccounts(response.items);
        }
        setCursor((prev) => ({ ...prev, accounts: response.cursor || null }));
        setHasMore((prev) => ({ ...prev, accounts: response.hasMore }));
      } else {
        const response = await getDeletedEvents(
          loadMore ? cursor.events || undefined : undefined
        );
        if (loadMore) {
          setEvents((prev) => [...prev, ...response.items]);
        } else {
          setEvents(response.items);
        }
        setCursor((prev) => ({ ...prev, events: response.cursor || null }));
        setHasMore((prev) => ({ ...prev, events: response.hasMore }));
      }
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(
        errorMessage ||
          t("management.loadError")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreDialog.type || !restoreDialog.id) return;

    try {
      setRestoring(restoreDialog.id.toString());
      if (restoreDialog.type === "account") {
        await restoreAccount(restoreDialog.id as number);
        toast.success(
          t("management.accountRestored", {
          })
        );
        setAccounts((prev) => prev.filter((acc) => acc.user_id !== restoreDialog.id));
      } else {
        await restoreEvent(restoreDialog.id as string);
        toast.success(
          t("management.eventRestored", {
          })
        );
        setEvents((prev) => prev.filter((evt) => evt.event_uuid !== restoreDialog.id));
      }
      setRestoreDialog({ open: false, type: null, id: null, name: null });
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(
        errorMessage ||
          t("management.restoreError")
      );
    } finally {
      setRestoring(null);
    }
  };

  const handleToggleFlag = async (key: string, nextEnabled: boolean) => {
    const previousFlags = featureFlags;

    // Optimistic update: flip immediately, roll back if the request fails.
    setFeatureFlags((prev) => (prev ? { ...prev, [key]: nextEnabled } : prev));
    setTogglingFlags((prev) => new Set(prev).add(key));

    try {
      const res = await updateFeatureFlag(key, nextEnabled);
      setFeatureFlags(res.flags);
      toast.success(t("management.featureFlagUpdated", { key }));
    } catch (error: unknown) {
      setFeatureFlags(previousFlags);
      const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(errorMessage || t("management.featureFlagUpdateError"));
    } finally {
      setTogglingFlags((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "PPp", { locale: dateLocale });
    } catch {
      return dateString;
    }
  };

  // Revenue buckets arrive as "YYYY-MM" keys
  const formatMonth = (month: string) => {
    try {
      return format(parseISO(`${month}-01`), "MMMM yyyy", { locale: dateLocale });
    } catch {
      return month;
    }
  };

  const isRetentionExpired = (retentionUntil: string | null) => {
    if (!retentionUntil) return false;
    return new Date(retentionUntil) < new Date();
  };

  // JUB-43: amounts are always RON-labeled explicitly by the API, but format
  // with the payment's own currency so nothing gets silently relabeled.
  const formatMoney = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat(currentLocale === "ro" ? "ro-RO" : "en-US", {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${currency}`;
    }
  };

  // Show loading spinner while checking permissions
  if (authLoading || checkingPermissions) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10">
        <AdminHeader currentLocale={currentLocale} active="management" />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {t("management.checkingPermissions")}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10">
      <AdminHeader currentLocale={currentLocale} active="management" />
      <main className="container mx-auto px-4 py-8 min-h-[calc(100vh-4rem)]">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">
            {t("management.title")}
          </h2>
          <p className="text-muted-foreground">
            {t("management.description", {
            })}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ManagementTabValue)}>
          <TabsList className="mb-6 flex h-auto flex-wrap gap-1">
            <TabsTrigger value="users">
              <Users className="mr-2 h-4 w-4" />
              {t("management.users") ?? "Users"}
            </TabsTrigger>
            <TabsTrigger value="revenue">
              <Wallet className="mr-2 h-4 w-4" />
              {t("management.revenue") ?? "Revenue"}
            </TabsTrigger>
            <TabsTrigger value="feature-flags">
              <Flag className="mr-2 h-4 w-4" />
              {t("management.featureFlags") ?? "Feature flags"}
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="mr-2 h-4 w-4" />
              {t("management.settings") ?? "Settings"}
            </TabsTrigger>
            <TabsTrigger value="accounts">
              <User className="mr-2 h-4 w-4" />
              {t("management.accounts")}
            </TabsTrigger>
            <TabsTrigger value="events">
              <Calendar className="mr-2 h-4 w-4" />
              {t("management.events")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>{t("management.users") ?? "Users"}</CardTitle>
                <CardDescription>
                  {t("management.usersDescription") ?? "All users with event count. Click to view profile and events."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative mb-4 max-w-md">
                  {loading && usersSearch ? (
                    <Loader2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                  ) : (
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  )}
                  <Input
                    type="text"
                    autoComplete="off"
                    data-testid="management-users-search"
                    aria-label={t("management.searchUsers")}
                    placeholder={t("management.searchUsers")}
                    value={usersSearchInput}
                    onChange={(e) => setUsersSearchInput(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {usersSearchInput ? (
                    <button
                      type="button"
                      aria-label={t("management.clearSearch")}
                      onClick={() => setUsersSearchInput("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
                {loading && usersList.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : usersList.length === 0 && usersSearch ? (
                  <div className="text-center py-12 text-muted-foreground" data-testid="management-users-empty-search">
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t("management.noUsersMatch", { search: usersSearch })}</p>
                    <Button variant="link" className="mt-2" onClick={() => setUsersSearchInput("")}>
                      {t("management.clearSearch")}
                    </Button>
                  </div>
                ) : usersList.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t("management.noUsers") ?? "No users yet."}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-md border overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left p-3 font-medium">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="-ml-2 h-8 font-medium"
                                onClick={() => {
                                  const col = "user_name";
                                  setUsersSortField(col);
                                  setUsersSortDirection((prev) => (usersSortField === col ? (prev === "asc" ? "desc" : "asc") : "desc"));
                                  setUsersCursor(null);
                                }}
                              >
                                {t("management.user")}
                                {usersSortField === "user_name" ? (
                                  usersSortDirection === "asc" ? <ArrowUp className="ml-2 h-4 w-4" /> : <ArrowDown className="ml-2 h-4 w-4" />
                                ) : (
                                  <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />
                                )}
                              </Button>
                            </th>
                            <th className="text-left p-3 font-medium">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="-ml-2 h-8 font-medium"
                                onClick={() => {
                                  const col = "user_email";
                                  setUsersSortField(col);
                                  setUsersSortDirection((prev) => (usersSortField === col ? (prev === "asc" ? "desc" : "asc") : "desc"));
                                  setUsersCursor(null);
                                }}
                              >
                                Email
                                {usersSortField === "user_email" ? (
                                  usersSortDirection === "asc" ? <ArrowUp className="ml-2 h-4 w-4" /> : <ArrowDown className="ml-2 h-4 w-4" />
                                ) : (
                                  <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />
                                )}
                              </Button>
                            </th>
                            <th className="text-left p-3 font-medium" title={t("management.eventsCount")}>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="-ml-2 h-8 font-medium"
                                onClick={() => {
                                  const col = "events_count";
                                  setUsersSortField(col);
                                  setUsersSortDirection((prev) => (usersSortField === col ? (prev === "asc" ? "desc" : "asc") : "desc"));
                                  setUsersCursor(null);
                                }}
                              >
                                {t("management.eventsCount")}
                                {usersSortField === "events_count" ? (
                                  usersSortDirection === "asc" ? <ArrowUp className="ml-2 h-4 w-4" /> : <ArrowDown className="ml-2 h-4 w-4" />
                                ) : (
                                  <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />
                                )}
                              </Button>
                            </th>
                            <th className="text-left p-3 font-medium text-muted-foreground text-xs whitespace-nowrap">
                              {t("management.eventsFree")}
                            </th>
                            <th className="text-left p-3 font-medium text-muted-foreground text-xs whitespace-nowrap">
                              {t("management.eventsStandard")}
                            </th>
                            <th className="text-left p-3 font-medium text-muted-foreground text-xs whitespace-nowrap">
                              {t("management.eventsPremium")}
                            </th>
                            <th className="text-left p-3 font-medium">
                              {t("management.credits")}
                            </th>
                            <th className="text-left p-3 font-medium">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="-ml-2 h-8 font-medium"
                                onClick={() => {
                                  const col = "created_at";
                                  setUsersSortField(col);
                                  setUsersSortDirection((prev) => (usersSortField === col ? (prev === "asc" ? "desc" : "asc") : "desc"));
                                  setUsersCursor(null);
                                }}
                              >
                                {t("management.created")}
                                {usersSortField === "created_at" ? (
                                  usersSortDirection === "asc" ? <ArrowUp className="ml-2 h-4 w-4" /> : <ArrowDown className="ml-2 h-4 w-4" />
                                ) : (
                                  <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />
                                )}
                              </Button>
                            </th>
                            <th className="text-left p-3 font-medium">{t("management.connected")}</th>
                            <th className="text-left p-3 font-medium">{t("management.status")}</th>
                            <th className="text-left p-3 font-medium">{t("common.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usersList.map((u) => {
                            const free = u.events_count_free ?? 0;
                            const standard = u.events_count_standard ?? 0;
                            const premium = u.events_count_premium ?? 0;
                            const creditsStd = u.credits_standard ?? 0;
                            const creditsPrem = u.credits_premium ?? 0;
                            const creditsLabel =
                              creditsStd > 0 || creditsPrem > 0
                                ? [creditsStd > 0 ? `${creditsStd} std` : null, creditsPrem > 0 ? `${creditsPrem} prem` : null]
                                    .filter(Boolean)
                                    .join(", ")
                                : "—";
                            return (
                              <tr key={u.user_id} className="border-b last:border-0 hover:bg-accent/30">
                                <td className="p-3">{u.user_name || "—"}</td>
                                <td className="p-3">{u.user_email}</td>
                                <td className="p-3">{u.events_count}</td>
                                <td className="p-3 text-muted-foreground text-center">{free}</td>
                                <td className="p-3 text-muted-foreground text-center">{standard}</td>
                                <td className="p-3 text-muted-foreground text-center">{premium}</td>
                                <td className="p-3 text-muted-foreground text-sm">{creditsLabel}</td>
                                <td className="p-3 text-muted-foreground">
                                  {u.created_at ? formatDate(u.created_at) : "—"}
                                </td>
                                <td className="p-3">
                                  {u.stripe_connected ? (
                                    <Badge className="bg-green-600 hover:bg-green-600/90 text-white border-transparent">
                                      {t("common.yes")}
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary">{t("common.no")}</Badge>
                                  )}
                                </td>
                                <td className="p-3">
                                  {u.user_active ? (
                                    <Badge className="bg-green-600 hover:bg-green-600/90 text-white border-transparent" data-testid={`management-user-status-${u.user_id}`}>
                                      {t("management.active")}
                                    </Badge>
                                  ) : (
                                    <Badge variant="destructive" data-testid={`management-user-status-${u.user_id}`}>
                                      {t("management.inactive")}
                                    </Badge>
                                  )}
                                </td>
                                <td className="p-3">
                                  <Link to={ADMIN_ROUTES.userProfile(currentLocale, u.user_id)}>
                                    <Button variant="outline" size="sm">
                                      <Link2 className="h-4 w-4 mr-1" />
                                      {t("management.viewProfile") ?? "View profile"}
                                    </Button>
                                  </Link>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {usersHasMore && (
                      <Button variant="outline" className="w-full" onClick={() => loadData(true)} disabled={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        {t("management.loadMore")}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="revenue">
            <Card>
              <CardHeader>
                <CardTitle>{t("management.revenue") ?? "Revenue"}</CardTitle>
                <CardDescription>{t("management.revenueDescription")}</CardDescription>
              </CardHeader>
              <CardContent>
                {loading && !revenue ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : !revenue ? (
                  <p className="text-muted-foreground">{t("management.loadError")}</p>
                ) : (
                  <div className="space-y-8">
                    {/* Headline stats */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div className="rounded-lg border p-4">
                        <p className="text-sm text-muted-foreground">{t("management.revenuePayingUsers")}</p>
                        <p className="text-2xl font-bold">{revenue.paying_users_count}</p>
                        <p className="text-xs text-muted-foreground mt-1">{t("management.revenuePayingUsersHint")}</p>
                      </div>
                      <div className="rounded-lg border p-4">
                        <p className="text-sm text-muted-foreground">{t("management.revenueCollected30d")}</p>
                        <p className="text-2xl font-bold">
                          {formatMoney(revenue.revenue.collected_last_30_days, revenue.currency)}
                        </p>
                      </div>
                      <div className="rounded-lg border p-4">
                        <p className="text-sm text-muted-foreground">{t("management.revenueCollectedMonth")}</p>
                        <p className="text-2xl font-bold">
                          {formatMoney(revenue.revenue.collected_current_calendar_month, revenue.currency)}
                        </p>
                      </div>
                      <div className="rounded-lg border p-4">
                        <p className="text-sm text-muted-foreground">{t("management.revenueCollectedAllTime")}</p>
                        <p className="text-2xl font-bold">
                          {formatMoney(revenue.revenue.total_collected_all_time, revenue.currency)}
                        </p>
                      </div>
                      <div className="rounded-lg border p-4">
                        <p className="text-sm text-muted-foreground">{t("management.revenueNewPaidEvents30d")}</p>
                        <p className="text-2xl font-bold">{revenue.revenue.new_paid_events_last_30_days}</p>
                      </div>
                    </div>

                    {revenue.revenue.excluded_non_ron_successful_count > 0 && (
                      <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 text-sm text-amber-800 dark:text-amber-300">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                          {t("management.revenueExcludedNonRon", {
                            count: revenue.revenue.excluded_non_ron_successful_count,
                          })}
                        </span>
                      </div>
                    )}

                    {/* MRR note */}
                    <div className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
                      <Info className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>{t("management.revenueMrrNote")}</span>
                    </div>

                    {/* Plan distribution */}
                    <div>
                      <h3 className="text-lg font-semibold mb-1">{t("management.revenuePlanDistribution")}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{t("management.revenuePlanDistributionHint")}</p>
                      <div className="flex flex-wrap gap-3">
                        <Badge variant="secondary" className="text-sm px-3 py-1">
                          {t("management.eventsFree")}: {revenue.plan_distribution.free}
                        </Badge>
                        <Badge className="text-sm px-3 py-1 bg-blue-600 hover:bg-blue-600/90 text-white border-transparent">
                          {t("management.eventsStandard")}: {revenue.plan_distribution.standard}
                        </Badge>
                        <Badge className="text-sm px-3 py-1 bg-purple-600 hover:bg-purple-600/90 text-white border-transparent">
                          {t("management.eventsPremium")}: {revenue.plan_distribution.premium}
                        </Badge>
                      </div>
                    </div>

                    {/* Monthly breakdown */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3">{t("management.revenueMonthlyBreakdown")}</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-2 font-medium">{t("management.revenueMonth")}</th>
                              <th className="text-left p-2 font-medium">{t("management.revenueAmount")}</th>
                              <th className="text-left p-2 font-medium">{t("management.revenuePaidCount")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {revenue.revenue.monthly_breakdown.map((bucket) => (
                              <tr key={bucket.month} className="border-b last:border-0">
                                <td className="p-2">{formatMonth(bucket.month)}</td>
                                <td className="p-2">{formatMoney(bucket.collected, revenue.currency)}</td>
                                <td className="p-2 text-muted-foreground">{bucket.paid_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Recent payments */}
                    <div>
                      <h3 className="text-lg font-semibold mb-1">{t("management.revenueRecentPayments")}</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        {t("management.revenueRecentPaymentsDescription")}
                      </p>
                      {revenue.recent_payments.length === 0 ? (
                        <p className="text-muted-foreground">{t("management.revenueNoRecentPayments")}</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-2 font-medium">{t("management.user")}</th>
                                <th className="text-left p-2 font-medium">{t("management.revenueEvent")}</th>
                                <th className="text-left p-2 font-medium">{t("management.revenuePlanColumn")}</th>
                                <th className="text-left p-2 font-medium">{t("management.revenueAmount")}</th>
                                <th className="text-left p-2 font-medium">{t("management.revenueDate")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {revenue.recent_payments.map((payment) => (
                                <tr key={payment.payment_id} className="border-b last:border-0 hover:bg-accent/30">
                                  <td className="p-2">
                                    {payment.user_id ? (
                                      <Link
                                        to={ADMIN_ROUTES.userProfile(currentLocale, payment.user_id)}
                                        className="text-primary hover:underline"
                                      >
                                        {payment.user_name || payment.user_email || `#${payment.user_id}`}
                                      </Link>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                  <td className="p-2 text-muted-foreground">{payment.event_name || "—"}</td>
                                  <td className="p-2">
                                    {payment.plan || "—"}
                                    {payment.voucher_applied && (
                                      <Badge variant="outline" className="ml-2 text-xs">
                                        {t("management.revenueVoucherApplied")}
                                      </Badge>
                                    )}
                                  </td>
                                  <td className="p-2">{formatMoney(payment.amount, payment.currency)}</td>
                                  <td className="p-2 text-muted-foreground">
                                    {payment.date ? formatDate(payment.date) : "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground">{t("management.revenueRefundsNote")}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="feature-flags">
            <Card>
              <CardHeader>
                <CardTitle>{t("management.featureFlags") ?? "Feature flags"}</CardTitle>
                <CardDescription>
                  {t("management.featureFlagsDescription") ?? "Global feature flags from config."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading && featureFlags === null ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : featureFlags && Object.keys(featureFlags).length > 0 ? (
                  <ul className="space-y-2">
                    {Object.entries(featureFlags).map(([key, value]) => (
                      <li key={key} className="flex items-center justify-between py-2 border-b last:border-0">
                        <code className="text-sm">{key}</code>
                        <div className="flex items-center gap-3">
                          <Badge variant={value ? "default" : "secondary"}>{value ? t("management.flagOn") : t("management.flagOff")}</Badge>
                          <Switch
                            checked={value}
                            disabled={togglingFlags.has(key)}
                            label={key}
                            onCheckedChange={(checked) => handleToggleFlag(key, checked)}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Flag className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t("management.noFeatureFlags") ?? "No feature flags defined."}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>{t("management.settings") ?? "Global settings"}</CardTitle>
                <CardDescription>
                  {t("management.settingsDescription") ?? "Global application settings (if any)."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading && globalSettings === null ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : globalSettings && Object.keys(globalSettings).length > 0 ? (
                  <pre className="text-sm rounded-md bg-muted p-4 overflow-auto">
                    {JSON.stringify(globalSettings, null, 2)}
                  </pre>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t("management.noSettings") ?? "No global settings defined."}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="accounts">
            <Card>
              <CardHeader>
                <CardTitle>
                  {t("management.deletedAccounts")}
                </CardTitle>
                <CardDescription>
                  {t("management.deletedAccountsDescription", {
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading && accounts.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : accounts.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Trash2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t("management.noDeletedAccounts")}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {accounts.map((account) => (
                      <div
                        key={account.user_id}
                        className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="font-semibold">{account.user_name}</h3>
                            <Badge variant="secondary" className="max-w-full break-all">{account.user_email}</Badge>
                            {isRetentionExpired(account.retention_until) && (
                              <Badge variant="destructive">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {t("management.retentionExpired")}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <p>
                              {t("management.deletedAt")}:{" "}
                              {formatDate(account.deleted_at)}
                            </p>
                            {account.retention_until && (
                              <p>
                                {t("management.retentionUntil")}:{" "}
                                {formatDate(account.retention_until)}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0 self-start sm:self-center"
                          onClick={() =>
                            setRestoreDialog({
                              open: true,
                              type: "account",
                              id: account.user_id,
                              name: account.user_name,
                            })
                          }
                          disabled={restoring === account.user_id.toString()}
                        >
                          {restoring === account.user_id.toString() ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RotateCcw className="h-4 w-4 mr-2" />
                          )}
                          {t("management.restore")}
                        </Button>
                      </div>
                    ))}
                    {hasMore.accounts && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => loadData(true)}
                        disabled={loading}
                      >
                        {loading ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : null}
                        {t("management.loadMore")}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="events">
            <Card>
              <CardHeader>
                <CardTitle>
                  {t("management.deletedEvents")}
                </CardTitle>
                <CardDescription>
                  {t("management.deletedEventsDescription")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading && events.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : events.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Trash2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t("management.noDeletedEvents")}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {events.map((event) => (
                      <div
                        key={event.event_uuid}
                        className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="font-semibold">{event.event_name}</h3>
                            <Badge variant="outline">ID: {event.event_id}</Badge>
                            {isRetentionExpired(event.retention_until) && (
                              <Badge variant="destructive">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {t("management.retentionExpired")}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <p>
                              {t("management.deletedAt")}:{" "}
                              {formatDate(event.deleted_at)}
                            </p>
                            {event.retention_until && (
                              <p>
                                {t("management.retentionUntil")}:{" "}
                                {formatDate(event.retention_until)}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0 self-start sm:self-center"
                          onClick={() =>
                            setRestoreDialog({
                              open: true,
                              type: "event",
                              id: event.event_uuid,
                              name: event.event_name,
                            })
                          }
                          disabled={restoring === event.event_uuid}
                        >
                          {restoring === event.event_uuid ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RotateCcw className="h-4 w-4 mr-2" />
                          )}
                          {t("management.restore")}
                        </Button>
                      </div>
                    ))}
                    {hasMore.events && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => loadData(true)}
                        disabled={loading}
                      >
                        {loading ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : null}
                        {t("management.loadMore")}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <AlertDialog open={restoreDialog.open} onOpenChange={(open) => setRestoreDialog({ ...restoreDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("management.confirmRestore")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {restoreDialog.type === "account"
                  ? t("management.confirmRestoreAccount", {
                      name: restoreDialog.name ?? "",
                    })
                  : t("management.confirmRestoreEvent", {
                      name: restoreDialog.name ?? "",
                    })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("common.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleRestore} disabled={!!restoring}>
                {restoring ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {t("management.restore")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}

